import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { NotificationRow } from "@/components/NotificationBits";
import { listNotifications, markAllNotificationsRead, markNotificationRead } from "@/lib/api";
import type { Notification } from "@/lib/types";

type Filter = "unread" | "all";

export default function NotificationDropdown({
  accessToken,
  onClose,
  onMarkedAllRead,
  onMarkedOneRead,
}: {
  accessToken: string;
  onClose: () => void;
  onMarkedAllRead: () => void;
  onMarkedOneRead: () => void;
}) {
  const [filter, setFilter] = useState<Filter>("unread");
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listNotifications({ unreadOnly: filter === "unread", limit: 5 }, accessToken)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter, accessToken]);

  async function markAllRead() {
    setItems((cur) => cur.map((n) => ({ ...n, read: true })));
    onMarkedAllRead();
    try {
      await markAllNotificationsRead(accessToken);
    } catch {
      /* optimistic — a transient failure here isn't worth rolling back for */
    }
  }

  function openRow(n: Notification) {
    if (!n.read) {
      setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      onMarkedOneRead();
      markNotificationRead(n.id, accessToken).catch(() => {});
    }
    onClose();
  }

  return (
    <div
      className="absolute right-0 top-full z-[200] mt-2 w-[340px] overflow-hidden rounded-xl"
      style={{ background: "var(--bg-elev)", border: "1px solid var(--border)", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}
    >
      <span className="pointer-events-none absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="flex items-center justify-between px-5 pt-4">
        <h3 className="font-display text-sm font-medium text-text">Notifications</h3>
        <button onClick={markAllRead} className="font-mono text-[10.5px] uppercase tracking-wide text-text/40 transition hover:text-primary">
          Mark all read
        </button>
      </div>

      <div className="flex gap-2 px-5 pb-3 pt-3">
        {(["unread", "all"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 font-display text-xs font-medium transition ${
              filter === f ? "bg-white/[0.08] text-text" : "border border-white/[0.13] text-text/45 hover:text-text/70"
            }`}
          >
            {f === "unread" ? "Unread" : "All"}
          </button>
        ))}
      </div>

      <div className="max-h-80 overflow-y-auto border-t border-white/[0.06]">
        {loading ? (
          <p className="px-5 py-6 text-center text-xs text-text/40">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-5 py-6 text-center text-xs text-text/40">
            {filter === "unread" ? "You're all caught up." : "No notifications yet."}
          </p>
        ) : (
          items.map((n, i) => (
            <div key={n.id} className={i > 0 ? "border-t border-[var(--border)]" : ""}>
              <NotificationRow n={n} dense onOpen={() => openRow(n)} token={accessToken}
                onResolved={() => { setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x))); onMarkedOneRead(); }} />
            </div>
          ))
        )}
      </div>

      <div className="border-t border-white/[0.06] py-3 text-center">
        <Link
          to="/notifications"
          onClick={onClose}
          className="font-display text-xs font-medium text-primary transition hover:text-primary-hover"
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}
